import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { CourtTask, BaseTask, User } from '@ee/common/models';
import { AccountType, CustomFieldType, TaskType } from '@ee/common/enums';
import { actorName } from '@ee/common/utils';

@Component({
  selector: 'ee-case-task',
  template: `
    <div class="case-task" [ngClass]="{ 'hide-border': hideBorder }">
      @if (completable) {
        <div class="complete-task" (click)="markCompleted()">
          @if (!!task?.marked_completed_date && !tempMarkIncomplete) {
            <i class="fa fa-check-circle"></i>
          } @else {
            <div class="unfinished" [ngClass]="{ 'temp-incomplete': tempMarkIncomplete }">
              <i class="far fa-circle"></i>
              <i class="fa fa-check-circle"></i>
            </div>
          }
        </div>
      }
      <div class="task-summary" [ngClass]="$safeNavigationMigration(task?.type)" (click)="onEdit.emit(task)">
        <div class="flex flex-row justify-between items-center">
          <div class="task-name-wrapper">
            @if (task?.required) {
              <span class="required" [tp]="task?.required ? 'Required' : ''">*</span>
            }
            <div class="task-name">
              {{ task?.name }}
            </div>
          </div>
          @if (assignedUsers?.length) {
            <div class="flex flex-row justify-start items-center">
              @for (user of assignedUsers; track user) {
                <span
                  class="small"
                  [tp]="user.display_name"
                  tpPlacement="top"
                  [tpDelay]="500"
                  [attr.data-letters]="user.display_name | dataLetters"
                  [ngStyle]="{ '--user-selector-color': user.color }"></span>
              }
            </div>
          }
        </div>
        @if (task?.description) {
          <div class="flex-1 task-description">
            {{ task.description }}
          </div>
        }
        @if (showThirdRow) {
          <div class="bottom-row">
            @if (task?.required) {
              <span class="required">Required</span>
            }
            @if (task?.due_date) {
              <div class="task-due-date">
                <span class="task-due-date-label"><i class="far fa-calendar-day"></i></span>
                <span>
                  {{ $safeNavigationMigration(task?.due_date) | dateTimeFromIso | dateTimeToFormat: 'MMM d, yyyy' }}
                </span>

                @if (!task?.marked_completed_date && !!task.days_remaining && task.days_remaining < 8) {
                  @if (task.days_remaining > 0) {
                    <span class="days-remaining">
                      {{ task.days_remaining }} day{{ task.days_remaining > 1 ? 's' : '' }} left
                    </span>
                  }
                  @if (task.days_remaining === 0) {
                    <span class="days-remaining">Due today</span>
                  }
                  @if (task.days_remaining < 0) {
                    <span class="days-remaining">
                      {{ task.days_remaining * -1 }} day{{ task.days_remaining < -1 ? 's' : '' }} overdue
                    </span>
                  }
                }
              </div>
            }
            @if (task?.remove_on_step_completion) {
              <i
                class="fas fa-octagon-xmark extra"
                tp="This task will be removed when step is processed unless it is marked as completed"></i>
            }
            @if (!!task?.marked_completed_date) {
              <div class="text-end italic flex-1" [tp]="'Completed by ' + markedCompletedBy">
                {{ markedCompletedBy }}
              </div>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .case-task {
        flex-direction: row;
        justify-content: stretch;
        align-items: flex-start;
        padding: 8px;
        border: 1px solid var(--color-outline);
        border-radius: 4px;
        background-color: var(--color-surface);
        display: flex;
        user-select: none;

        &.hide-border {
          border: none;
        }

        .complete-task {
          height: 24px;
          margin-right: 8px;
          z-index: 1;
          cursor: pointer;

          .fa-check-circle,
          .fa-circle {
            font-size: 24px;
          }

          .fa-circle {
            color: var(--color-on-surface-disabled);
          }

          .fa-check-circle {
            color: var(--c-green);
          }

          .unfinished {
            display: inline-block;
            width: auto;

            .fa-circle {
              display: block;
            }

            .fa-check-circle {
              display: none;
            }

            &:hover {
              .fa-circle {
                display: none;
              }

              .fa-check-circle {
                display: block;
              }
            }

            &.temp-incomplete:hover .fa-check-circle {
              .fa-circle {
                display: block;
              }

              .fa-check-circle {
                display: none;
              }
            }
          }
        }

        &:hover {
          background-color: var(--color-surface-container-high);
        }

        .task-summary {
          position: relative;
          color: var(--color-on-surface);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          cursor: pointer;
          flex: 1;

          .task-name-wrapper {
            white-space: nowrap;
            overflow: hidden;
            display: flex;
            flex-direction: row;
            flex: 1;

            .task-name {
              font-weight: 600;
              color: var(--color-on-surface);
              font-size: 14px;
              text-overflow: ellipsis;
              white-space: nowrap;
              overflow: hidden;
              max-width: 100%;
            }

            .required {
              color: var(--primary-red);
              font-size: 14px;
            }
          }

          .task-description {
            overflow: hidden;
            width: 95%;
            white-space: nowrap;
            text-overflow: ellipsis;
            color: var(--color-on-surface-variant);
            opacity: 0.7;
            font-size: 12px;
            font-weight: 500;
          }

          .roles-row,
          .bottom-row {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
          }

          .roles-row {
            margin-top: 2px;

            .mat-chips.status {
              row-gap: 4px;
              font-size: 10px;
              padding: 4px 0;
            }
          }

          .bottom-row {
            margin-top: 2px;
            font-size: 12px;

            .required {
              color: var(--primary-red);
              font-weight: 600;
              margin-right: 6px;
            }

            .fa.extra {
              font-weight: 600;
              font-size: 12px;
              display: inline-block;
              margin-right: 6px;
            }

            .task-due-date {
              text-wrap: nowrap;
              align-self: start;
              color: var(--color-on-surface-variant);
              font-size: 12px;
              font-weight: 500;
              margin-right: 6px;

              .task-due-date-label {
                font-size: 12px;
                font-weight: 500;
                margin-right: 2px;
              }

              .days-remaining {
                font-weight: 500;
                color: var(--primary-red);
                margin-left: 4px;
              }
            }
          }
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CaseTaskComponent implements OnInit, OnChanges {
  /**
   * Who marked the task complete. EE-531: `actor_display` names the completion actor support-aware, and is the
   * marker only while the task is not yet completed; once completed it names the completer instead.
   */
  get markedCompletedBy(): string | null {
    const display = this.task?.completed_date ? null : this.task?.actor_display;
    return actorName(this.task?.marked_completed_by, display);
  }

  public isOverdue = false;
  public taskTypes = TaskType;
  public fieldTypes = CustomFieldType;
  public assignedUsers: User[] = [];
  public showThirdRow = false;
  public tempMarkIncomplete = false;

  @Input({ required: true })
  public task: CourtTask | BaseTask;

  @Input()
  public editable = true;

  @Input()
  public completable = true;

  @Input()
  public deletable = true;

  @Input() hideBorder = false;

  @Input()
  public dueDateOnlyChangeable = false;

  @Input()
  public users: User[] = [];

  @Output()
  public onEdit = new EventEmitter<BaseTask>();

  @Output()
  public onMarkCompleted = new EventEmitter<BaseTask>();

  ngOnInit() {
    this.isOverdue = this.task?.days_remaining && this.task?.days_remaining <= 3;
    this.showThirdRow =
      this.task?.required ||
      !!this.task?.due_date ||
      this.task?.remove_on_step_completion ||
      !!this.task?.marked_completed_date;
  }

  ngOnChanges() {
    this.assignedUsers = this.users
      ?.filter((user) => this.task.assigned_users.includes(user.id))
      .sort((a, b) => a.display_name.localeCompare(b.display_name));
    this.tempMarkIncomplete = false;
  }

  markCompleted() {
    this.task.marked_completed_date = !this.task.marked_completed_date ? new Date().toISOString() : null;
    if (!this.task.marked_completed_date) {
      this.tempMarkIncomplete = true;
    }
    this.onMarkCompleted.emit(this.task);
  }

  protected readonly AccountType = AccountType;
  protected readonly CustomFieldType = CustomFieldType;
}
